import {
  Component,
  OnInit,
  OnDestroy,
  forwardRef,
  signal,
  computed,
  ElementRef,
  ViewChild,
  HostListener,
  ChangeDetectionStrategy,
  Input,
} from '@angular/core';
import {
  ControlValueAccessor,
  NG_VALUE_ACCESSOR,
  NG_VALIDATORS,
  Validator,
  AbstractControl,
  ValidationErrors,
  ReactiveFormsModule,
  FormControl,
} from '@angular/forms';

import { Subject, takeUntil } from 'rxjs';

// ─── Types ────────────────────────────────────────────────────────────────────
export interface CountryData {
  name: string;
  iso2: string;
  dialCode: string;
  flag: string;
  format?: string; // optional mask  # = digit
  minLen: number;
  maxLen: number;
  priority?: number; // for shared dial codes (e.g. +1)
}

// ─── Utilities ────────────────────────────────────────────────────────────────
export function stripDigits(v: string): string {
  return v.replace(/\D/g, '');
}

export function applyMask(digits: string, mask?: string): string {
  if (!mask) return digits;
  let out = '',
    di = 0;
  for (let i = 0; i < mask.length && di < digits.length; i++) {
    out += mask[i] === '#' ? digits[di++] : mask[i];
  }
  return out;
}

function isoToFlag(iso: string): string {
  return iso
    .toUpperCase()
    .split('')
    .map((c) => String.fromCodePoint(0x1f1e6 - 65 + c.charCodeAt(0)))
    .join('');
}

// ─── Complete country list (249 countries/territories, ITU data) ──────────────
// Format: [name, iso2, dialCode, format-mask?, minLen, maxLen, priority?]
// priority only needed when multiple countries share a dialCode (e.g. +1, +7)
const RAW: [
  string,
  string,
  string,
  string | undefined,
  number,
  number,
  number?,
][] = [
  ['Afghanistan', 'AF', '+93', '## ### ####', 9, 9],
  ['Albania', 'AL', '+355', '### ### ####', 9, 9],
  ['Algeria', 'DZ', '+213', '#### ## ## ##', 9, 9],
  ['American Samoa', 'AS', '+1', '(###) ###-####', 10, 10, 5],
  ['Andorra', 'AD', '+376', '### ###', 6, 6],
  ['Angola', 'AO', '+244', '### ### ###', 9, 9],
  ['Anguilla', 'AI', '+1', '(###) ###-####', 10, 10, 6],
  ['Antigua & Barbuda', 'AG', '+1', '(###) ###-####', 10, 10, 7],
  ['Argentina', 'AR', '+54', '## ####-####', 10, 10],
  ['Armenia', 'AM', '+374', '## ######', 8, 8],
  ['Aruba', 'AW', '+297', '### ####', 7, 7],
  ['Australia', 'AU', '+61', '#### ### ###', 9, 9],
  ['Austria', 'AT', '+43', '#### ######', 10, 13],
  ['Azerbaijan', 'AZ', '+994', '## ### ## ##', 9, 9],
  ['Bahamas', 'BS', '+1', '(###) ###-####', 10, 10, 8],
  ['Bahrain', 'BH', '+973', '#### ####', 8, 8],
  ['Bangladesh', 'BD', '+880', '####-######', 10, 10],
  ['Barbados', 'BB', '+1', '(###) ###-####', 10, 10, 9],
  ['Belarus', 'BY', '+375', '## ###-##-##', 9, 9],
  ['Belgium', 'BE', '+32', '#### ## ## ##', 9, 9],
  ['Belize', 'BZ', '+501', '###-####', 7, 7],
  ['Benin', 'BJ', '+229', '## ## ## ##', 8, 8],
  ['Bermuda', 'BM', '+1', '(###) ###-####', 10, 10, 10],
  ['Bhutan', 'BT', '+975', '## ### ###', 8, 8],
  ['Bolivia', 'BO', '+591', '########', 8, 8],
  ['Bosnia & Herzegovina', 'BA', '+387', '### ###-###', 9, 9],
  ['Botswana', 'BW', '+267', '## ### ###', 8, 8],
  ['Brazil', 'BR', '+55', '(##) #####-####', 11, 11],
  ['British Indian Ocean Territory', 'IO', '+246', '### ####', 7, 7],
  ['British Virgin Islands', 'VG', '+1', '(###) ###-####', 10, 10, 11],
  ['Brunei', 'BN', '+673', '### ####', 7, 7],
  ['Bulgaria', 'BG', '+359', '### ### ###', 9, 9],
  ['Burkina Faso', 'BF', '+226', '## ## ## ##', 8, 8],
  ['Burundi', 'BI', '+257', '## ## ## ##', 8, 8],
  ['Cambodia', 'KH', '+855', '## ### ###', 8, 9],
  ['Cameroon', 'CM', '+237', '#### ## ## ##', 9, 9],
  ['Canada', 'CA', '+1', '(###) ###-####', 10, 10, 1],
  ['Cape Verde', 'CV', '+238', '### ## ##', 7, 7],
  ['Caribbean Netherlands', 'BQ', '+599', '### ####', 7, 7, 1],
  ['Cayman Islands', 'KY', '+1', '(###) ###-####', 10, 10, 12],
  ['Central African Republic', 'CF', '+236', '## ## ## ##', 8, 8],
  ['Chad', 'TD', '+235', '## ## ## ##', 8, 8],
  ['Chile', 'CL', '+56', '# #### ####', 9, 9],
  ['China', 'CN', '+86', '### #### ####', 11, 11],
  ['Christmas Island', 'CX', '+61', '#### ### ###', 9, 9, 2],
  ['Cocos Islands', 'CC', '+61', '#### ### ###', 9, 9, 3],
  ['Colombia', 'CO', '+57', '### ### ####', 10, 10],
  ['Comoros', 'KM', '+269', '### ## ##', 7, 7],
  ['Congo (DRC)', 'CD', '+243', '### ### ###', 9, 9],
  ['Congo', 'CG', '+242', '## ### ####', 9, 9],
  ['Cook Islands', 'CK', '+682', '## ###', 5, 5],
  ['Costa Rica', 'CR', '+506', '#### ####', 8, 8],
  ['Croatia', 'HR', '+385', '## ### ####', 9, 9],
  ['Cuba', 'CU', '+53', '## ### ####', 8, 8],
  ['Curaçao', 'CW', '+599', '#### ####', 8, 8],
  ['Cyprus', 'CY', '+357', '## ######', 8, 8],
  ['Czech Republic', 'CZ', '+420', '### ### ###', 9, 9],
  ['Denmark', 'DK', '+45', '## ## ## ##', 8, 8],
  ['Djibouti', 'DJ', '+253', '## ## ## ##', 8, 8],
  ['Dominica', 'DM', '+1', '(###) ###-####', 10, 10, 13],
  ['Dominican Republic', 'DO', '+1', '(###) ###-####', 10, 10, 14],
  ['Ecuador', 'EC', '+593', '## ### ####', 9, 9],
  ['Egypt', 'EG', '+20', '### #### ####', 10, 10],
  ['El Salvador', 'SV', '+503', '#### ####', 8, 8],
  ['Equatorial Guinea', 'GQ', '+240', '### ### ###', 9, 9],
  ['Eritrea', 'ER', '+291', '## ### ###', 7, 7],
  ['Estonia', 'EE', '+372', '#### ####', 7, 8],
  ['Eswatini', 'SZ', '+268', '#### ####', 8, 8],
  ['Ethiopia', 'ET', '+251', '## ### ####', 9, 9],
  ['Falkland Islands', 'FK', '+500', '#####', 5, 5],
  ['Faroe Islands', 'FO', '+298', '### ###', 6, 6],
  ['Fiji', 'FJ', '+679', '### ####', 7, 7],
  ['Finland', 'FI', '+358', '## ### ####', 9, 10],
  ['France', 'FR', '+33', '# ## ## ## ##', 9, 9],
  ['French Guiana', 'GF', '+594', '####-##-##-##', 9, 9],
  ['French Polynesia', 'PF', '+689', '## ## ## ##', 8, 8],
  ['Gabon', 'GA', '+241', '## ## ## ##', 8, 8],
  ['Gambia', 'GM', '+220', '### ####', 7, 7],
  ['Georgia', 'GE', '+995', '### ## ## ##', 9, 9],
  ['Germany', 'DE', '+49', '#### ########', 10, 12],
  ['Ghana', 'GH', '+233', '## ### ####', 9, 9],
  ['Gibraltar', 'GI', '+350', '########', 8, 8],
  ['Greece', 'GR', '+30', '### ### ####', 10, 10],
  ['Greenland', 'GL', '+299', '## ## ##', 6, 6],
  ['Grenada', 'GD', '+1', '(###) ###-####', 10, 10, 15],
  ['Guadeloupe', 'GP', '+590', '### ## ## ##', 9, 9],
  ['Guam', 'GU', '+1', '(###) ###-####', 10, 10, 16],
  ['Guatemala', 'GT', '+502', '#### ####', 8, 8],
  ['Guernsey', 'GG', '+44', '##### ######', 10, 10, 1],
  ['Guinea', 'GN', '+224', '### ## ## ##', 9, 9],
  ['Guinea-Bissau', 'GW', '+245', '### ####', 7, 7],
  ['Guyana', 'GY', '+592', '### ####', 7, 7],
  ['Haiti', 'HT', '+509', '## ## ####', 8, 8],
  ['Honduras', 'HN', '+504', '#### ####', 8, 8],
  ['Hong Kong', 'HK', '+852', '#### ####', 8, 8],
  ['Hungary', 'HU', '+36', '## ### ####', 9, 9],
  ['Iceland', 'IS', '+354', '### ####', 7, 7],
  ['India', 'IN', '+91', '##### #####', 10, 10],
  ['Indonesia', 'ID', '+62', '####-####-####', 10, 12],
  ['Iran', 'IR', '+98', '#### ### ####', 10, 10],
  ['Iraq', 'IQ', '+964', '### ### ####', 10, 10],
  ['Ireland', 'IE', '+353', '## ### ####', 9, 9],
  ['Isle of Man', 'IM', '+44', '##### ######', 10, 10, 2],
  ['Israel', 'IL', '+972', '##-###-####', 9, 9],
  ['Italy', 'IT', '+39', '### ### ####', 10, 10],
  ['Ivory Coast', 'CI', '+225', '## ## ## ## ##', 10, 10],
  ['Jamaica', 'JM', '+1', '(###) ###-####', 10, 10, 17],
  ['Japan', 'JP', '+81', '###-####-####', 10, 11],
  ['Jersey', 'JE', '+44', '##### ######', 10, 10, 3],
  ['Jordan', 'JO', '+962', '# #### ####', 9, 9],
  ['Kazakhstan', 'KZ', '+7', '### ###-##-##', 10, 10, 1],
  ['Kenya', 'KE', '+254', '### ### ###', 9, 9],
  ['Kiribati', 'KI', '+686', '## ###', 5, 5],
  ['Kosovo', 'XK', '+383', '## ### ###', 8, 9],
  ['Kuwait', 'KW', '+965', '#### ####', 8, 8],
  ['Kyrgyzstan', 'KG', '+996', '### ### ###', 9, 9],
  ['Laos', 'LA', '+856', '## ## ### ###', 10, 10],
  ['Latvia', 'LV', '+371', '## ### ###', 8, 8],
  ['Lebanon', 'LB', '+961', '## ### ###', 7, 8],
  ['Lesotho', 'LS', '+266', '#### ####', 8, 8],
  ['Liberia', 'LR', '+231', '### ### ####', 9, 9],
  ['Libya', 'LY', '+218', '## #### ####', 9, 9],
  ['Liechtenstein', 'LI', '+423', '### ####', 7, 7],
  ['Lithuania', 'LT', '+370', '### ## ###', 8, 8],
  ['Luxembourg', 'LU', '+352', '### ### ###', 9, 9],
  ['Macau', 'MO', '+853', '#### ####', 8, 8],
  ['Madagascar', 'MG', '+261', '## ## ### ##', 9, 9],
  ['Malawi', 'MW', '+265', '#### ### ###', 9, 9],
  ['Malaysia', 'MY', '+60', '##-#### ####', 9, 10],
  ['Maldives', 'MV', '+960', '### ####', 7, 7],
  ['Mali', 'ML', '+223', '## ## ## ##', 8, 8],
  ['Malta', 'MT', '+356', '#### ####', 8, 8],
  ['Marshall Islands', 'MH', '+692', '###-####', 7, 7],
  ['Martinique', 'MQ', '+596', '### ## ## ##', 9, 9],
  ['Mauritania', 'MR', '+222', '## ## ## ##', 8, 8],
  ['Mauritius', 'MU', '+230', '#### ####', 8, 8],
  ['Mayotte', 'YT', '+262', '### ## ## ##', 9, 9],
  ['Mexico', 'MX', '+52', '## #### ####', 10, 10],
  ['Micronesia', 'FM', '+691', '### ####', 7, 7],
  ['Moldova', 'MD', '+373', '## ### ###', 8, 8],
  ['Monaco', 'MC', '+377', '## ## ## ##', 8, 8],
  ['Mongolia', 'MN', '+976', '## ## ####', 8, 8],
  ['Montenegro', 'ME', '+382', '## ### ###', 8, 9],
  ['Montserrat', 'MS', '+1', '(###) ###-####', 10, 10, 18],
  ['Morocco', 'MA', '+212', '####-######', 9, 9],
  ['Mozambique', 'MZ', '+258', '## ### ####', 9, 9],
  ['Myanmar', 'MM', '+95', '## ### ####', 9, 10],
  ['Namibia', 'NA', '+264', '## ### ####', 9, 9],
  ['Nauru', 'NR', '+674', '### ####', 7, 7],
  ['Nepal', 'NP', '+977', '##-######## ', 10, 10],
  ['Netherlands', 'NL', '+31', '# ## ## ## ##', 9, 9],
  ['New Caledonia', 'NC', '+687', '## ## ##', 6, 6],
  ['New Zealand', 'NZ', '+64', '## ### ####', 8, 10],
  ['Nicaragua', 'NI', '+505', '#### ####', 8, 8],
  ['Niger', 'NE', '+227', '## ## ## ##', 8, 8],
  ['Nigeria', 'NG', '+234', '### ### ####', 10, 10],
  ['Niue', 'NU', '+683', '####', 4, 4],
  ['Norfolk Island', 'NF', '+672', '## ####', 6, 6],
  ['North Korea', 'KP', '+850', '### ### ####', 10, 10],
  ['North Macedonia', 'MK', '+389', '## ### ###', 8, 8],
  ['Northern Mariana Islands', 'MP', '+1', '(###) ###-####', 10, 10, 19],
  ['Norway', 'NO', '+47', '### ## ###', 8, 8],
  ['Oman', 'OM', '+968', '#### ####', 8, 8],
  ['Pakistan', 'PK', '+92', '### #######', 10, 10],
  ['Palau', 'PW', '+680', '### ####', 7, 7],
  ['Palestine', 'PS', '+970', '### ### ###', 9, 9],
  ['Panama', 'PA', '+507', '#### ####', 8, 8],
  ['Papua New Guinea', 'PG', '+675', '### ####', 7, 8],
  ['Paraguay', 'PY', '+595', '### ### ###', 9, 9],
  ['Peru', 'PE', '+51', '### ### ###', 9, 9],
  ['Philippines', 'PH', '+63', '### ### ####', 10, 10],
  ['Poland', 'PL', '+48', '### ### ###', 9, 9],
  ['Portugal', 'PT', '+351', '### ### ###', 9, 9],
  ['Puerto Rico', 'PR', '+1', '(###) ###-####', 10, 10, 20],
  ['Qatar', 'QA', '+974', '#### ####', 8, 8],
  ['Réunion', 'RE', '+262', '### ## ## ##', 9, 9, 1],
  ['Romania', 'RO', '+40', '### ### ###', 9, 9],
  ['Russia', 'RU', '+7', '### ###-##-##', 10, 10],
  ['Rwanda', 'RW', '+250', '### ### ###', 9, 9],
  ['Saint Kitts & Nevis', 'KN', '+1', '(###) ###-####', 10, 10, 21],
  ['Saint Lucia', 'LC', '+1', '(###) ###-####', 10, 10, 22],
  ['Saint Martin', 'MF', '+590', '### ## ## ##', 9, 9, 1],
  ['Saint Pierre & Miquelon', 'PM', '+508', '## ## ##', 6, 6],
  ['Saint Vincent', 'VC', '+1', '(###) ###-####', 10, 10, 23],
  ['Samoa', 'WS', '+685', '## ####', 6, 7],
  ['San Marino', 'SM', '+378', '## ## ## ##', 8, 9],
  ['São Tomé & Príncipe', 'ST', '+239', '## #####', 7, 7],
  ['Saudi Arabia', 'SA', '+966', '## ### ####', 9, 9],
  ['Senegal', 'SN', '+221', '## ### ## ##', 9, 9],
  ['Serbia', 'RS', '+381', '## ### ####', 9, 9],
  ['Seychelles', 'SC', '+248', '# ### ###', 7, 7],
  ['Sierra Leone', 'SL', '+232', '## ######', 8, 8],
  ['Singapore', 'SG', '+65', '#### ####', 8, 8],
  ['Sint Maarten', 'SX', '+1', '(###) ###-####', 10, 10, 24],
  ['Slovakia', 'SK', '+421', '### ### ###', 9, 9],
  ['Slovenia', 'SI', '+386', '## ### ###', 8, 8],
  ['Solomon Islands', 'SB', '+677', '## ####', 6, 7],
  ['Somalia', 'SO', '+252', '## ### ###', 8, 9],
  ['South Africa', 'ZA', '+27', '## ### ####', 9, 9],
  ['South Korea', 'KR', '+82', '##-####-####', 10, 11],
  ['South Sudan', 'SS', '+211', '### ### ###', 9, 9],
  ['Spain', 'ES', '+34', '### ### ###', 9, 9],
  ['Sri Lanka', 'LK', '+94', '## ### ####', 9, 9],
  ['Sudan', 'SD', '+249', '## ### ####', 9, 9],
  ['Suriname', 'SR', '+597', '### ####', 7, 7],
  ['Sweden', 'SE', '+46', '##-### ## ##', 9, 9],
  ['Switzerland', 'CH', '+41', '## ### ## ##', 9, 9],
  ['Syria', 'SY', '+963', '### ### ###', 9, 9],
  ['Taiwan', 'TW', '+886', '####-######', 9, 9],
  ['Tajikistan', 'TJ', '+992', '## ### ####', 9, 9],
  ['Tanzania', 'TZ', '+255', '### ### ###', 9, 9],
  ['Thailand', 'TH', '+66', '##-###-####', 9, 9],
  ['Timor-Leste', 'TL', '+670', '### ####', 7, 7],
  ['Togo', 'TG', '+228', '## ## ## ##', 8, 8],
  ['Tokelau', 'TK', '+690', '####', 4, 4],
  ['Tonga', 'TO', '+676', '### ####', 7, 7],
  ['Trinidad & Tobago', 'TT', '+1', '(###) ###-####', 10, 10, 25],
  ['Tunisia', 'TN', '+216', '## ### ###', 8, 8],
  ['Turkey', 'TR', '+90', '### ### ## ##', 10, 10],
  ['Turkmenistan', 'TM', '+993', '## ######', 8, 8],
  ['Turks & Caicos Islands', 'TC', '+1', '(###) ###-####', 10, 10, 26],
  ['Tuvalu', 'TV', '+688', '## ###', 5, 6],
  ['Uganda', 'UG', '+256', '### ### ###', 9, 9],
  ['Ukraine', 'UA', '+380', '## ### ## ##', 9, 9],
  ['United Arab Emirates', 'AE', '+971', '## ### ####', 9, 9],
  ['United Kingdom', 'GB', '+44', '#### ######', 10, 10],
  ['United States', 'US', '+1', '(###) ###-####', 10, 10, 0],
  ['Uruguay', 'UY', '+598', '# ### ## ##', 8, 8],
  ['US Virgin Islands', 'VI', '+1', '(###) ###-####', 10, 10, 27],
  ['Uzbekistan', 'UZ', '+998', '## ### ## ##', 9, 9],
  ['Vanuatu', 'VU', '+678', '## ####', 6, 7],
  ['Vatican City', 'VA', '+39', '## #### ####', 10, 10, 1],
  ['Venezuela', 'VE', '+58', '###-#######', 10, 10],
  ['Vietnam', 'VN', '+84', '### ### ###', 9, 10],
  ['Wallis & Futuna', 'WF', '+681', '## ## ##', 6, 6],
  ['Western Sahara', 'EH', '+212', '####-######', 9, 9, 1],
  ['Yemen', 'YE', '+967', '### ### ###', 9, 9],
  ['Zambia', 'ZM', '+260', '### ### ###', 9, 9],
  ['Zimbabwe', 'ZW', '+263', '## ### ####', 9, 9],
];

export const COUNTRIES: CountryData[] = RAW.map(
  ([name, iso2, dialCode, format, minLen, maxLen, priority]) => ({
    name,
    iso2,
    dialCode,
    format,
    minLen,
    maxLen,
    priority: priority ?? 0,
    flag: isoToFlag(iso2),
  }),
).sort((a, b) => a.name.localeCompare(b.name));

// ─── Component ────────────────────────────────────────────────────────────────
@Component({
  selector: 'app-phone-number-input',
  standalone: true,
  imports: [ReactiveFormsModule],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => PhoneNumberInputComponent),
      multi: true,
    },
    {
      provide: NG_VALIDATORS,
      useExisting: forwardRef(() => PhoneNumberInputComponent),
      multi: true,
    },
  ],
  templateUrl: './phone-number-input.component.html',
  styleUrl: './phone-number-input.component.scss',
})
export class PhoneNumberInputComponent
  implements OnInit, OnDestroy, ControlValueAccessor, Validator
{
  @ViewChild('numInput') numRef!: ElementRef<HTMLInputElement>;
  @ViewChild('searchEl') searchRef!: ElementRef<HTMLInputElement>;

  /** Bind: [isInvalid]="submitted && !!f['phone_number'].errors" */
  @Input() isInvalid = false;
  /** Bind: [parentErrors]="f['phone_number'].errors" */
  @Input() parentErrors: Record<string, any> | null = null;
  /** Default country iso2 code */
  @Input() set defaultCountry(iso: string) {
    const c = COUNTRIES.find((x) => x.iso2.toUpperCase() === iso.toUpperCase());
    if (c) this.country.set(c);
  }

  readonly uid = Math.random().toString(36).slice(2, 8);
  readonly applyMask = applyMask; // expose to template

  // ── Signals ─────────────────────────────────────────────────────────────────
  country = signal<CountryData>(COUNTRIES.find((c) => c.iso2 === 'IN')!);
  digits = signal('');
  focused = signal(false);
  open = signal(false);
  disabled = signal(false);
  touched = signal(false);

  searchCtrl = new FormControl('', { nonNullable: true });
  searchQuery = signal('');

  display = computed(() => applyMask(this.digits(), this.country().format));
  filtered = computed(() => {
    const q = this.searchQuery().toLowerCase().trim();
    if (!q) return COUNTRIES;
    return COUNTRIES.filter(
      (c) =>
        c.name.toLowerCase().includes(q) ||
        c.dialCode.replace('+', '').startsWith(q.replace('+', '')) ||
        c.iso2.toLowerCase() === q,
    );
  });

  innerError = computed(() => {
    if (!this.touched()) return false;
    const d = this.digits(),
      c = this.country();
    if (!d.length) return false;
    return d.length < c.minLen || d.length > c.maxLen;
  });

  // ── CVA ─────────────────────────────────────────────────────────────────────
  private _onChange: (v: string) => void = () => {};
  private _onTouched: () => void = () => {};
  private readonly destroy$ = new Subject<void>();

  constructor(private readonly elRef: ElementRef) {}

  ngOnInit() {
    this.searchCtrl.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe((v) => this.searchQuery.set(v));
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }

  writeValue(val: string | null) {
    if (!val) {
      this.digits.set('');
      return;
    }
    // Try to match E.164 prefix to a country
    const cleaned = val.replace(/\D/g, '');
    const sortedCountries = [...COUNTRIES].sort(
      (a, b) => b.dialCode.length - a.dialCode.length,
    );
    for (const c of sortedCountries) {
      const code = c.dialCode.replace('+', '');
      if (cleaned.startsWith(code) && (c.priority ?? 0) === 0) {
        this.country.set(c);
        this.digits.set(cleaned.slice(code.length));
        return;
      }
    }
    this.digits.set(cleaned);
  }

  registerOnChange(fn: any) {
    this._onChange = fn;
  }
  registerOnTouched(fn: any) {
    this._onTouched = fn;
  }
  setDisabledState(v: boolean) {
    this.disabled.set(v);
  }

  validate(_: AbstractControl): ValidationErrors | null {
    const d = this.digits(),
      c = this.country();
    if (!d.length) return null;
    if (d.length < c.minLen)
      return { phoneTooShort: { min: c.minLen, actual: d.length } };
    if (d.length > c.maxLen)
      return { phoneTooLong: { max: c.maxLen, actual: d.length } };
    return null;
  }

  // ── Events ───────────────────────────────────────────────────────────────────
  onBlur() {
    this.focused.set(false);
    this.touched.set(true);
    this._onTouched();
  }

  onInput(e: Event) {
    const raw = (e.target as HTMLInputElement).value;
    const digits = stripDigits(raw).slice(0, this.country().maxLen);
    this.digits.set(digits);
    this.emit();
    requestAnimationFrame(() => {
      if (this.numRef)
        this.numRef.nativeElement.value = applyMask(
          digits,
          this.country().format,
        );
    });
  }

  onKey(e: KeyboardEvent) {
    const allow = [
      'Backspace',
      'Delete',
      'Tab',
      'Escape',
      'Enter',
      'ArrowLeft',
      'ArrowRight',
      'Home',
      'End',
    ];
    if (allow.includes(e.key)) return;
    if ((e.ctrlKey || e.metaKey) && 'acvx'.includes(e.key.toLowerCase()))
      return;
    if (!/^\d$/.test(e.key)) e.preventDefault();
  }

  onPaste(e: ClipboardEvent) {
    e.preventDefault();
    const digits = stripDigits(e.clipboardData?.getData('text') ?? '').slice(
      0,
      this.country().maxLen,
    );
    this.digits.set(digits);
    this.emit();
    if (this.numRef)
      this.numRef.nativeElement.value = applyMask(
        digits,
        this.country().format,
      );
  }

  onSearchKey(e: KeyboardEvent) {
    if (e.key === 'Escape') {
      this.open.set(false);
      this.numRef?.nativeElement.focus();
    }
  }

  toggleOpen(e: MouseEvent) {
    e.stopPropagation();
    this.open.update((v) => !v);
    if (this.open()) {
      this.searchCtrl.setValue('');
      setTimeout(() => this.searchRef?.nativeElement.focus(), 40);
    }
  }

  pick(c: CountryData) {
    this.country.set(c);
    this.digits.set('');
    this.open.set(false);
    this.searchCtrl.setValue('');
    this.searchQuery.set('');
    this.emit();
    setTimeout(() => this.numRef?.nativeElement.focus(), 40);
  }

  clear() {
    this.digits.set('');
    this.emit();
    this.numRef?.nativeElement.focus();
  }

  /** Full E.164 value, e.g. "+919876543210" — use this for API calls */
  get e164(): string {
    const d = this.digits();
    return d ? `${this.country().dialCode}${d}` : '';
  }

  @HostListener('document:click', ['$event'])
  onDocClick(e: MouseEvent) {
    if (!this.elRef.nativeElement.contains(e.target as Node))
      this.open.set(false);
  }

  @HostListener('document:keydown.escape')
  onEsc() {
    if (this.open()) this.open.set(false);
  }

  /** trackBy for @for — avoids ?? operator in template (causes Angular compiler bug) */
  trackCountry(c: CountryData): string {
    return c.iso2 + '_' + (c.priority ?? 0);
  }

  /** Placeholder derived from format mask — keeps template clean */
  getPlaceholder(): string {
    const fmt = this.country().format;
    return fmt ? applyMask('##########', fmt) : 'Phone number';
  }

  // private emit() { this._onChange(this.digits()); }
  private emit() {
    this._onChange(this.e164);
  }

  reset(iso: string = 'IN') {
    const c = COUNTRIES.find((x) => x.iso2.toUpperCase() === iso.toUpperCase());
    if (c) this.country.set(c);
    this.digits.set('');
    this.touched.set(false);
    this.open.set(false);
    this._onChange('');
  }
}
